import React, { useState } from 'react';
import { useLanguage } from '../LanguageContext';
import { useAuth } from '../AuthContext';
import { can } from '../permissions';
import { setLiveAdStatus, updateLiveAd } from '../api';

// Shared ad creative + detail modal for the Channels pages (Snapchat + TikTok).
// Plays the video when there is one (TikTok preview_url), otherwise shows the image;
// lists every detail the overview carries; and — for users who can publish — lets you
// pause/resume the ad and edit its text/headline, reusing the campaign live endpoints.
// The parent passes the ad object, the platform, and an onChanged() refresh callback.
export default function AdDetailModal({ ad, platform, onClose, onChanged }) {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const ar = lang === 'ar';
  const L = (a, e) => (ar ? a : e);
  const canEdit = can(user, 'campaigns.publish');

  const groupId = platform === 'snapchat' ? (ad.adsquad_id || ad.adgroupId) : (ad.adgroup_id || ad.adgroupId);
  const name = ad.name || ad.ad_name || '';
  const active = ad.status === 'ENABLE' || ad.status === 'ACTIVE' || ad.status === 'STATUS_ENABLE';
  const text = ad.ad_text || ad.creative_headline || '';
  const cta = ad.call_to_action || '';
  const landing = ad.landing_page_url || ad.creative_url || '';
  const m = ad.metrics || {};

  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const [err, setErr] = useState(null);

  const toggle = async () => {
    setBusy(true); setErr(null);
    const res = await setLiveAdStatus(ad.ad_id, !active, platform, groupId);
    setBusy(false);
    if (res.error || res.ok === false) { setErr(res.message || res.error || L('فشل', 'Failed')); return; }
    onClose(); onChanged && onChanged();
  };
  const saveText = async () => {
    setBusy(true); setErr(null);
    const fields = platform === 'snapchat'
      ? { creativeId: ad.creative_id, headline: draft }
      : { text: draft, adgroupId: groupId };
    const res = await updateLiveAd(ad.ad_id, fields, platform);
    setBusy(false);
    if (res.error || res.ok === false) { setErr(res.message || res.error || L('فشل', 'Failed')); return; }
    if (res.note) window.alert(res.note);
    onClose(); onChanged && onChanged();
  };

  const Row = ({ label, children }) => (
    <div style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: '0.8rem' }}>
      <span style={{ color: 'var(--text-secondary)', minWidth: 92, flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', minWidth: 0, wordBreak: 'break-word' }}>{children}</span>
    </div>
  );
  const num = (v) => (v == null ? '—' : Number(v).toLocaleString());

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, overflowY: 'auto', cursor: 'zoom-out' }}>
      <div onClick={e => e.stopPropagation()} style={{ cursor: 'auto', background: 'var(--bg-card)', borderRadius: 16, maxWidth: 460, width: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 70px rgba(0,0,0,0.5)' }}>
        {/* Media */}
        <div style={{ position: 'relative', background: '#000', borderRadius: '16px 16px 0 0', overflow: 'hidden', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {ad.video_url
            ? <video src={ad.video_url} poster={ad.thumbnail_url || undefined} controls autoPlay playsInline style={{ width: '100%', maxHeight: '52vh', background: '#000' }} />
            : ad.thumbnail_url
              ? <img src={ad.thumbnail_url} alt="" style={{ width: '100%', maxHeight: '52vh', objectFit: 'contain' }} />
              : <div style={{ color: '#fff', opacity: 0.7, fontSize: '0.85rem', padding: 40, textAlign: 'center' }}>
                  {ad.media_type === 'video' ? '▶ ' : '🖼 '}
                  {L('لا تتوفر معاينة لهذا الإعلان (إعلانات Smart+ الديناميكية لا توفر صورة عبر الـAPI).', 'No preview available (dynamic Smart+ ads expose no still via the API).')}
                </div>}
          <button onClick={onClose} style={{ position: 'absolute', top: 10, insetInlineEnd: 10, width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
        </div>

        <div style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{name || L('إعلان', 'Ad')}</span>
            <span style={{ fontSize: '0.64rem', fontWeight: 700, padding: '2px 10px', borderRadius: 20, background: (active ? '#22c55e' : '#6b7280') + '22', color: active ? '#16a34a' : '#6b7280' }}>
              {active ? L('نشط', 'Active') : L('متوقف', 'Paused')}
            </span>
          </div>

          {(ad.ad_format || ad.creative_type) && <Row label={L('النوع', 'Format')}>{ad.ad_format || ad.creative_type}{ad.duration ? ` · ${Math.round(ad.duration)}s` : ''}</Row>}
          {text && !editing && <Row label={L('النص', 'Text')}>{text}</Row>}
          {cta && <Row label={L('زر الحث', 'CTA')}>{String(cta).replace(/_/g, ' ')}</Row>}
          {landing && <Row label={L('الوجهة', 'Landing')}><a href={landing} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{String(landing).slice(0, 60)}</a></Row>}
          <Row label={L('مرات الظهور', 'Impressions')}>{num(m.impressions)}</Row>
          <Row label={platform === 'snapchat' ? L('السحب', 'Swipes') : L('النقرات', 'Clicks')}>{num(m.swipes ?? m.clicks)}</Row>
          <Row label={L('الإنفاق', 'Spend')}>{m.spend != null ? Number(m.spend).toFixed(2) + ' ' + L('ر.س', 'SAR') : '—'}</Row>
          <Row label={L('معرّف الإعلان', 'Ad ID')}>{ad.ad_id}</Row>

          {/* Edit */}
          {canEdit && (
            <div style={{ marginTop: 14 }}>
              {err && <div style={{ padding: 8, borderRadius: 8, background: '#ef444422', color: '#ef4444', fontSize: '0.78rem', marginBottom: 8 }}>{err}</div>}
              {editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input value={draft} onChange={e => setDraft(e.target.value)} autoFocus
                    placeholder={platform === 'snapchat' ? L('العنوان (حتى 34 حرفًا)', 'Headline (max 34)') : L('نص الإعلان', 'Ad text')} />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={saveText} disabled={busy || !draft.trim()}>{busy ? '…' : L('حفظ', 'Save')}</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); setDraft(text); }}>{L('إلغاء', 'Cancel')}</button>
                  </div>
                  {platform === 'snapchat' && <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>{L('تعديل العنوان يُعيد مراجعة كل إعلان يستخدم نفس التصميم.', 'Editing the headline re-reviews every ad using this creative.')}</div>}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setDraft(text); setEditing(true); }}>{L('تعديل النص', 'Edit text')}</button>
                  <button className="btn btn-sm" onClick={toggle} disabled={busy}
                    style={{ background: active ? '#ef4444' : '#22c55e', color: '#fff' }}>{busy ? '…' : (active ? L('إيقاف الإعلان', 'Pause ad') : L('تشغيل الإعلان', 'Resume ad'))}</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
